import { useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import { SquigglyBackdrop, findBoundaryIndex } from "../Squiggly";
import { IMAGE_WIDTHS, blockCss, headingsOf, pageDims, paginate } from "./model";

export function spanSegments(text, spans) {
  const t = text || "";
  const ranges = [];
  (spans || []).forEach((sp) => {
    const idx = findBoundaryIndex(t, sp.text || "", 0);
    if (idx >= 0 && !ranges.some((r) => idx < r.end && idx + sp.text.length > r.start)) ranges.push({ start: idx, end: idx + sp.text.length, sp });
  });
  ranges.sort((a, b) => a.start - b.start);
  const segs = []; let pos = 0;
  ranges.forEach((r) => { if (r.start > pos) segs.push({ t: t.slice(pos, r.start) }); segs.push({ t: t.slice(r.start, r.end), sp: r.sp }); pos = r.end; });
  if (pos < t.length) segs.push({ t: t.slice(pos) });
  return segs;
}

function SpanBackdrop({ value, spans, style }) {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none whitespace-pre-wrap break-words overflow-hidden" style={style}>
      {spanSegments(value, spans).map((sg, i) => sg.sp ? (
        <span key={i} style={{ fontWeight: sg.sp.bold ? 700 : undefined, fontStyle: sg.sp.italic ? "italic" : undefined, textDecoration: sg.sp.underline ? "underline" : undefined, color: sg.sp.color || undefined }}>{sg.t}</span>
      ) : <span key={i}>{sg.t}</span>)}
    </div>
  );
}

function AutoTextarea({ value, onChange, style, onFocus, onSelectText, testid, issues, readOnly, spans, blockId }) {
  const ref = useRef(null);
  const fit = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } };
  const report = (e) => { const el = e.target; onSelectText?.({ blockId, start: el.selectionStart, end: el.selectionEnd, text: el.value.substring(el.selectionStart, el.selectionEnd) }); };
  useEffect(fit, [value, style.fontSize, style.fontFamily, style.lineHeight]);
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.ResizeObserver) return;
    let lastW = el.clientWidth;
    // Only refit on WIDTH changes: reacting to our own height writes causes a
    // "ResizeObserver loop completed with undelivered notifications" error.
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w !== lastW) { lastW = w; requestAnimationFrame(fit); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="relative flex-1 min-w-0">
      {spans && spans.length > 0 && <SpanBackdrop value={value} spans={spans} style={style} />}
      {issues && issues.length > 0 && <SquigglyBackdrop value={value} issues={issues} style={style} />}
      <textarea ref={ref} data-testid={testid} rows={1} value={value} readOnly={readOnly} onFocus={onFocus} onChange={(e) => onChange(e.target.value)}
        onSelect={report} onKeyUp={report} onMouseUp={report} onBlur={report}
        style={spans && spans.length > 0 ? { ...style, color: "transparent", caretColor: style.color || "#000" } : style} className="relative w-full bg-transparent resize-none outline-none border-none overflow-hidden placeholder:text-slate-400" />
    </div>
  );
}

export function Ruler({ dims }) {
  const inches = Math.round(dims.width / 64);
  return (
    <div className="mx-auto h-5 relative bg-white border-b border-[#c7c7c7] mb-1 text-[9px] text-slate-500 select-none" style={{ width: dims.width }} data-testid="studio-ruler">
      <div className="absolute inset-y-0 bg-[#e8eaed]" style={{ left: 0, width: dims.margin }} />
      <div className="absolute inset-y-0 bg-[#e8eaed]" style={{ right: 0, width: dims.margin }} />
      {Array.from({ length: inches * 2 + 1 }).map((_, i) => (
        <div key={i} className="absolute bottom-0" style={{ left: i * 32 }}>
          <div className={`w-px bg-slate-500 ${i % 2 ? "h-1.5" : "h-2.5"}`} />
          {i % 2 === 0 && i > 0 && i < inches * 2 && <span className="absolute -top-3 -translate-x-1/2">{i / 2}</span>}
        </div>
      ))}
      <div className="absolute -bottom-0.5 w-0 h-0 border-l-4 border-r-4 border-b-[6px] border-transparent border-b-[#1a73e8]" style={{ left: dims.margin - 4 }} />
      <div className="absolute -bottom-0.5 w-0 h-0 border-l-4 border-r-4 border-b-[6px] border-transparent border-b-[#1a73e8]" style={{ right: dims.margin - 4 }} />
    </div>
  );
}

function ImageBlock({ b, isSel, select, act, readOnly, config }) {
  const url = (config.gallery || []).find((g) => g.id === b.src)?.url;
  const w = IMAGE_WIDTHS[b.size || "medium"];
  const float = b.wrap === "wrap" ? "left" : "none";
  return (
    <div onClick={(e) => { e.stopPropagation(); select(); }} data-testid={`studio-image-block-${b.id}`}
      className={`my-2 ${b.wrap === "break" ? "clear-both" : ""} ${b.wrap === "inline" || !b.wrap ? "text-center" : ""}`}
      style={{ float, marginRight: float === "left" ? 16 : 0, maxWidth: "100%" }}>
      <img src={url} alt={b.caption || "Inserted image"} style={{ width: w, maxWidth: "100%" }} className={`inline-block rounded-sm ${isSel ? "ring-2 ring-[#1a73e8]" : ""}`} />
      {(isSel || b.caption) && (
        <input data-testid={`studio-image-caption-${b.id}`} value={b.caption || ""} readOnly={readOnly} placeholder="Add a caption…" onChange={(e) => act.patchBlock(b.id, { caption: e.target.value })}
          className="block w-full mt-1 text-center text-[11px] italic text-slate-600 bg-transparent outline-none placeholder:text-slate-400" style={{ maxWidth: w }} />
      )}
      {isSel && !readOnly && (
        <div className="mt-1 inline-flex flex-wrap items-center gap-1 bg-white border border-slate-200 rounded-xl shadow px-1.5 py-0.5 text-[11px]" style={{ maxWidth: w }} onClick={(e) => e.stopPropagation()}>
          {config.imageSizes.map((s) => <button key={s.id} data-testid={`studio-image-size-${s.id}`} onClick={() => act.patchBlock(b.id, { size: s.id })} className={`px-1.5 rounded ${b.size === s.id ? "bg-[#d3e3fd]" : "hover:bg-slate-100"}`}>{s.name}</button>)}
          <span className="w-px h-3 bg-slate-300" />
          {config.wraps.map((s) => <button key={s.id} data-testid={`studio-image-wrap-${s.id}`} onClick={() => act.patchBlock(b.id, { wrap: s.id })} className={`px-1.5 rounded ${(b.wrap || "inline") === s.id ? "bg-[#d3e3fd]" : "hover:bg-slate-100"}`}>{s.name}</button>)}
          <span className="w-px h-3 bg-slate-300" />
          <button data-testid={`studio-image-delete-${b.id}`} onClick={() => act.removeBlock(b.id)} className="p-0.5 text-slate-500 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
        </div>
      )}
    </div>
  );
}

function TocBlock({ b, blocks, isSel, select, act }) {
  const hs = headingsOf(blocks).filter((h) => h.style !== "title");
  return (
    <div onClick={(e) => { e.stopPropagation(); select(); }} data-testid="studio-toc-block" className={`my-3 p-3 rounded border ${isSel ? "border-[#1a73e8] bg-[#e8f0fe]/40" : "border-transparent hover:bg-slate-50"}`}>
      {hs.length === 0 && <p className="text-[12px] text-slate-500 italic">Add headings to see them in the table of contents.</p>}
      {hs.map((h) => (
        <div key={h.id} className="flex items-baseline gap-2 text-[13px] text-[#1a73e8]" style={{ paddingLeft: { h1: 0, h2: 18, h3: 36 }[h.style] || 0 }}>
          <span>{h.text || "(untitled heading)"}</span><span className="flex-1 border-b border-dotted border-slate-400" /><span className="text-slate-700">{h.page}</span>
        </div>
      ))}
      {isSel && <div className="mt-2 flex gap-2 text-[11px]"><span className="text-slate-500">Table of contents · updates as you edit</span><button data-testid="studio-toc-remove" onClick={() => act.removeBlock(b.id)} className="text-red-600 hover:underline">Remove</button></div>}
    </div>
  );
}

export default function Page({ doc, selectedId, setSelectedId, act, config, issues, pageRef, zoom, onSelectText, commentedIds }) {
  const dims = pageDims(doc.page);
  const readOnly = doc.mode === "viewing";
  const pages = paginate(doc.blocks);
  let numberCount = 0;
  const renderBlock = (b) => {
    const isSel = b.id === selectedId;
    const select = () => setSelectedId(b.id);
    if (b.type === "image") return <ImageBlock key={b.id} b={b} isSel={isSel} select={select} act={act} readOnly={readOnly} config={config} />;
    if (b.type === "toc") return <TocBlock key={b.id} b={b} blocks={doc.blocks} isSel={isSel} select={select} act={act} />;
    if (b.type === "table") {
      return (
        <div key={b.id} className={`my-3 clear-both ${isSel ? "ring-2 ring-[#1a73e8]/40 rounded" : ""}`} onClick={(e) => { e.stopPropagation(); select(); }}>
          <table className="border-collapse w-full"><tbody>
            {b.cells.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => (
              <td key={ci} className="border border-slate-500 p-0 bg-white"><input data-testid={`studio-cell-${ri}-${ci}`} value={cell} readOnly={readOnly} onChange={(e) => act.setCell(b.id, ri, ci, e.target.value)} className="w-full px-2 py-1.5 text-[13px] outline-none bg-transparent text-slate-900" /></td>
            ))}</tr>)}
          </tbody></table>
        </div>
      );
    }
    let prefix = null;
    if (b.type === "bullet") prefix = <span className="mt-0.5 select-none text-slate-800" style={{ marginLeft: (b.fmt?.indent || 0) * 36 }}>•</span>;
    if (b.type === "number") { numberCount += 1; prefix = <span className="mt-0.5 select-none text-slate-800" style={{ marginLeft: (b.fmt?.indent || 0) * 36 }}>{numberCount}.</span>; }
    if (b.type === "check") prefix = <input type="checkbox" className="mt-1 accent-[#1a73e8]" onClick={(e) => e.stopPropagation()} />;
    const css = blockCss(b);
    if (prefix) css.paddingLeft = 0;
    const commented = commentedIds.has(b.id);
    return (
      <div key={b.id} onClick={() => select()} data-testid={`studio-block-row-${b.id}`}
        className={`flex gap-2 rounded px-1 -mx-1 py-0.5 mb-0.5 cursor-text transition-colors ${isSel ? "ring-2 ring-[#1a73e8]/50 bg-[#e8f0fe]/40" : "hover:bg-slate-50"} ${commented ? "border-r-4 border-[#fbbc04]" : ""}`}
        style={{ marginTop: b.style === "title" ? 4 : b.style === "h1" ? 14 : b.style === "h2" ? 10 : b.style === "h3" ? 8 : 0, paddingRight: commented ? 6 : undefined }}>
        {prefix}
        <AutoTextarea testid={`studio-block-${b.id}`} value={b.text} readOnly={readOnly} onChange={(t) => act.patchBlock(b.id, { text: t })} onFocus={select} onSelectText={onSelectText}
          style={css} issues={(issues || []).filter((iss) => iss.blockId === b.id)} spans={b.spans} blockId={b.id} />
      </div>
    );
  };

  return (
    <div className="flex-1 min-w-0 overflow-auto hq-scrollbar bg-[#f9fbfd] max-h-[640px]" onClick={() => setSelectedId(selectedId)} data-testid="studio-canvas">
      <div style={{ zoom: zoom / 100, minWidth: dims.width + 24 }} className="py-4 px-3">
        {doc.showRuler && <Ruler dims={dims} />}
        <div ref={pageRef} className="space-y-4">
          {pages.map((pg, pi) => (
            <div key={pi} data-testid={`studio-page-${pi + 1}`} className="mx-auto bg-white text-slate-900 shadow-[0_1px_3px_rgba(60,64,67,.3),0_4px_8px_3px_rgba(60,64,67,.15)] relative"
              style={{ width: dims.width, minHeight: dims.height, padding: dims.margin, boxSizing: "border-box" }}>
              {pi === 0 ? (
                <input data-testid="studio-header-input" value={doc.header} readOnly={readOnly} onChange={(e) => act.setDoc({ ...doc, header: e.target.value })} placeholder="Header (optional)…"
                  className="w-full text-[11px] text-slate-500 bg-transparent border-b border-dashed border-slate-200 pb-1 mb-3 outline-none placeholder:text-slate-300" />
              ) : (
                <div className="text-[11px] text-slate-500 border-b border-dashed border-slate-200 pb-1 mb-3 min-h-[18px]">{doc.header}</div>
              )}
              {pi > 0 && <div className="-mt-1 mb-2 text-[10px] uppercase tracking-widest text-[#1a73e8]/70 select-none" data-testid={`studio-pagebreak-marker-${pi}`}>— page break —</div>}
              {pg.map(renderBlock)}
              <div className="clear-both" />
              {doc.footerPageNumber && <div className="absolute left-0 right-0 text-center text-[11px] text-slate-500" style={{ bottom: dims.margin / 2 }}>{pi + 1}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
